/* ==========================================================================
   AmTape Creators & Affiliate landing — behavior
   Vanilla JS, no dependencies. Scoped to the .atc root element.
   Safe to load once; re-running init on the same root is a no-op.
   ========================================================================== */
(function () {
  'use strict';

  function initAtc(root) {
    if (!root || root.dataset.atcInit === '1') return;
    root.dataset.atcInit = '1';

    /* ---- mobile menu ---------------------------------------------------- */
    var burger = root.querySelector('[data-atc-burger]');
    var menu = root.querySelector('[data-atc-menu]');
    if (burger && menu) {
      var closeMenu = function () {
        menu.setAttribute('hidden', '');
        burger.setAttribute('aria-expanded', 'false');
      };
      burger.addEventListener('click', function () {
        var open = menu.hasAttribute('hidden');
        if (open) {
          menu.removeAttribute('hidden');
          burger.setAttribute('aria-expanded', 'true');
        } else {
          closeMenu();
        }
      });
      menu.querySelectorAll('a').forEach(function (a) {
        a.addEventListener('click', closeMenu);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !menu.hasAttribute('hidden')) {
          closeMenu();
          burger.focus();
        }
      });
      document.addEventListener('click', function (e) {
        if (menu.hasAttribute('hidden')) return;
        if (menu.contains(e.target) || burger.contains(e.target)) return;
        closeMenu();
      });
    }
    /* ---- scroll progress bar ------------------------------------------- */
    var bar = root.querySelector('[data-atc-progress]');
    function onScroll() {
      if (!bar) return;
      var se = document.scrollingElement || document.documentElement;
      var max = se.scrollHeight - window.innerHeight;
      bar.style.width = (max > 0 ? (se.scrollTop / max) * 100 : 0) + '%';
    }
    if (bar) {
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    /* ---- dynamic header height calculation ----------------------------- */
    var stickyHeader = document.querySelector('.section-header');
    function updateHeaderHeight() {
      if (stickyHeader) {
        var height = stickyHeader.offsetHeight;
        document.documentElement.style.setProperty('--atc-header-height', height + 'px');
      }
    }
    if (stickyHeader) {
      updateHeaderHeight();
      window.addEventListener('scroll', updateHeaderHeight, { passive: true });
      window.addEventListener('resize', updateHeaderHeight, { passive: true });
      if ('ResizeObserver' in window) {
        var ro = new ResizeObserver(updateHeaderHeight);
        ro.observe(stickyHeader);
      }
    }
    /* ---- scroll-spy nav ------------------------------------------------- */
    var navLinks = Array.prototype.slice.call(root.querySelectorAll('[data-atc-nav] a'));
    if (navLinks.length && 'IntersectionObserver' in window) {
      var map = {};
      navLinks.forEach(function (a) {
        var id = a.getAttribute('href').slice(1);
        if (id) map[id] = a;
      });
      var spy = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            navLinks.forEach(function (a) {
              a.classList.remove('is-active');
              a.removeAttribute('aria-current');
            });
            var a = map[e.target.id];
            if (a) {
              a.classList.add('is-active');
              a.setAttribute('aria-current', 'true');
            }
          }
        });
      }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 });
      Object.keys(map).forEach(function (id) {
        var s = root.querySelector('#' + CSS.escape(id));
        if (s) spy.observe(s);
      });
    }

    /* ---- scroll reveal -------------------------------------------------- */
    var reveals = Array.prototype.slice.call(root.querySelectorAll('.atc-reveal'));
    if (reveals.length && 'IntersectionObserver' in window) {
      reveals.forEach(function (el) {
        var delay = el.getAttribute('data-atc-delay') || '0';
        el.style.transitionDelay = delay + 'ms';
      });
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            io.unobserve(e.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });
      reveals.forEach(function (el) { io.observe(el); });
    }

    /* ---- affiliate code copy-to-clipboard ------------------------------- */
    function copyText(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text);
      }
      // Fallback for non-secure contexts / older browsers
      return new Promise(function (resolve, reject) {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try {
          if (document.execCommand('copy')) resolve();
          else reject(new Error('copy command failed'));
        } catch (err) {
          reject(err);
        }
        document.body.removeChild(ta);
      });
    }

    var codeBlock = root.querySelector('.atc-code');
    if (codeBlock) {
      var codeValEl = codeBlock.querySelector('.atc-code-value');
      var copyStatus = root.querySelector('[data-atc-copy-status]');
      if (codeValEl) {
        var copyTimeout = null;
        codeBlock.addEventListener('click', function () {
          var code = codeValEl.textContent.trim();
          copyText(code).then(function () {
            if (copyTimeout) clearTimeout(copyTimeout);
            codeBlock.classList.add('is-copied');
            if (copyStatus) copyStatus.textContent = 'Code ' + code + ' copied to clipboard';
            copyTimeout = setTimeout(function () {
              codeBlock.classList.remove('is-copied');
              if (copyStatus) copyStatus.textContent = '';
            }, 2000);
          }).catch(function (err) {
            console.error('Could not copy text: ', err);
          });
        });
      }
    }

    /* ---- FAQ accordion -------------------------------------------------- */
    var faqItems = Array.prototype.slice.call(root.querySelectorAll('[data-atc-faq]'));
    faqItems.forEach(function (item) {
      var btn = item.querySelector('.atc-faq-q');
      var panel = item.querySelector('.atc-faq-a');
      if (!btn || !panel) return;
      btn.addEventListener('click', function () {
        var isOpen = !panel.hasAttribute('hidden');
        // close all
        faqItems.forEach(function (other) {
          var b = other.querySelector('.atc-faq-q');
          if (b) b.setAttribute('aria-expanded', 'false');
          var p = other.querySelector('.atc-faq-a');
          if (p) p.setAttribute('hidden', '');
        });
        // open this one (unless it was already open → toggle closed)
        if (!isOpen) {
          btn.setAttribute('aria-expanded', 'true');
          panel.removeAttribute('hidden');
        }
      });
    });
  }

  function boot(scope) {
    (scope || document).querySelectorAll('.atc').forEach(initAtc);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* ---- Shopify theme editor support ------------------------------------
     When a merchant edits section settings, Shopify replaces the section's
     markup in place and fires `shopify:section:load` on the section root.
     Since the new nodes never had `data-atc-init`, we just re-run init on
     whatever `.atc` element is inside the reloaded section. */
  document.addEventListener('shopify:section:load', function (evt) {
    boot(evt.target);
  });

  // expose for manual re-init
  window.AmTapeCreators = { init: initAtc, boot: boot };
})();
